import { bytes32Schema, resolveLaunchDraft, type LaunchConfig, type LaunchDraftInput, type PreparedCall } from '@hoodmorning/contracts';
import { decodeFunctionData, encodeAbiParameters, encodeFunctionData, getAddress, isAddressEqual, keccak256, parseAbi } from 'viem';

const preset = { name: 'preset', type: 'tuple', components: [
  { name: 'saleInventory', type: 'uint128' }, { name: 'lpInventory', type: 'uint128' }, { name: 'netQuoteTarget', type: 'uint128' },
] } as const;
const policy = { name: 'policy', type: 'tuple', components: [
  { name: 'tiers', type: 'tuple[]', components: [{ name: 'threshold', type: 'uint256' }, { name: 'baseFeeBps', type: 'uint16' }] },
  { name: 'creatorShareBps', type: 'uint16' }, { name: 'creatorChargeCapBps', type: 'uint16' },
] } as const;
export const launchFactoryCreateAbi = [{ type: 'function', name: 'createLaunch', stateMutability: 'nonpayable', inputs: [{ name: 'p', type: 'tuple', components: [
  { name: 'name', type: 'string' }, { name: 'symbol', type: 'string' }, { name: 'metadataHash', type: 'bytes32' },
  { name: 'quote', type: 'address' }, { name: 'quoteDecimals', type: 'uint8' }, preset, policy,
  { name: 'creatorChargeBps', type: 'uint16' }, { name: 'recipients', type: 'address[]' }, { name: 'recipientWeightsBps', type: 'uint16[]' },
] }], outputs: [{ name: 'curve', type: 'address' }, { name: 'token', type: 'address' }] }] as const;

export const launchFactoryFirstBuyAbi = [{ type: 'function', name: 'createLaunchWithFirstBuy', stateMutability: 'payable',
  inputs: [launchFactoryCreateAbi[0].inputs[0], { name: 'maximumGross', type: 'uint128' }, { name: 'minimumBaseOut', type: 'uint128' },
    { name: 'maximumFeeBps', type: 'uint16' }, { name: 'deadline', type: 'uint256' }, { name: 'nativeFunding', type: 'bool' }],
  outputs: [...launchFactoryCreateAbi[0].outputs, { name: 'firstBuy', type: 'tuple', components: [
    { name: 'spentGross', type: 'uint128' }, { name: 'netQuote', type: 'uint128' }, { name: 'feeQuote', type: 'uint128' },
    { name: 'refund', type: 'uint128' }, { name: 'baseOut', type: 'uint128' }, { name: 'totalFeeBps', type: 'uint16' }, { name: 'tier', type: 'uint256' },
  ] }],
}] as const;
export const launchFactoryCheckedCreateAbi = [{ type: 'function', name: 'createLaunchChecked', stateMutability: 'nonpayable',
  inputs: [...launchFactoryCreateAbi[0].inputs, { name: 'expectedAdapter', type: 'address' },
    { name: 'expectedCodeHash', type: 'bytes32' }, { name: 'deadline', type: 'uint256' }],
  outputs: launchFactoryCreateAbi[0].outputs,
}] as const;
export const launchFactoryCheckedFirstBuyAbi = [{ type: 'function', name: 'createLaunchWithFirstBuyChecked', stateMutability: 'payable',
  inputs: [...launchFactoryFirstBuyAbi[0].inputs, { name: 'expectedAdapter', type: 'address' },
    { name: 'expectedCodeHash', type: 'bytes32' }],
  outputs: launchFactoryFirstBuyAbi[0].outputs,
}] as const;
const approveAbi = parseAbi(['function approve(address spender,uint256 amount) returns (bool)']);

function checkedContext(input: {
  expectedAdapter: string; expectedCodeHash: `0x${string}`;
  deadlineUnixSeconds: bigint; nowUnixSeconds: bigint;
}) {
  const expectedAdapter = getAddress(input.expectedAdapter);
  bytes32Schema.parse(input.expectedCodeHash);
  if (expectedAdapter === '0x0000000000000000000000000000000000000000'
    || /^0x0{64}$/i.test(input.expectedCodeHash)) throw new Error('Invalid checked launch adapter pin');
  if (input.nowUnixSeconds < 0n || input.deadlineUnixSeconds <= input.nowUnixSeconds
    || input.deadlineUnixSeconds > input.nowUnixSeconds + 300n) throw new Error('Checked launch deadline must be within five minutes');
  return { expectedAdapter, expectedCodeHash: input.expectedCodeHash, deadlineUnixSeconds: input.deadlineUnixSeconds };
}

/** ABI encoding only. On-chain allowlist/code/owner checks and simulation are still mandatory. */
export function encodeApprovedLaunch(input: {
  config: LaunchConfig; draft: LaunchDraftInput; releaseId: string; metadataHash: `0x${string}`;
}): { call: PreparedCall; presetHash: `0x${string}`; templatePolicyHash: `0x${string}` } {
  const resolved = resolveLaunchDraft(input.config, input.draft);
  const release = input.config.releases.find(item => item.id === input.releaseId);
  if (!release) throw new Error('Unknown factory release');
  bytes32Schema.parse(input.metadataHash);
  if (resolved.draft.creatorFirstBuy) throw new Error('Atomic paid first-buy orchestration is not implemented yet');
  if (resolved.policy.lpFeeBps !== 0) throw new Error('This curve release does not implement a separate LP fee');
  const presetValue = { saleInventory: BigInt(resolved.preset.curveAllocation), lpInventory: BigInt(resolved.preset.lpAllocation), netQuoteTarget: BigInt(resolved.preset.graduationTarget) };
  if (presetValue.saleInventory <= presetValue.lpInventory) throw new Error('Curve sale inventory must exceed LP inventory');
  const policyValue = { tiers: resolved.policy.tiers.map(tier => ({ threshold: BigInt(tier.threshold), baseFeeBps: tier.baseFeeBps })), creatorShareBps: resolved.policy.creatorShareBps, creatorChargeCapBps: resolved.policy.optionalCreatorChargeCapBps };
  const presetHash = keccak256(encodeAbiParameters([preset], [presetValue]));
  const templatePolicyHash = keccak256(encodeAbiParameters([policy], [policyValue]));
  if (presetHash.toLowerCase() !== resolved.preset.presetHash.toLowerCase()) throw new Error('Preset content hash mismatch');
  if (templatePolicyHash.toLowerCase() !== resolved.policy.policyHash.toLowerCase()) throw new Error('Policy template content hash mismatch');
  return {
    presetHash, templatePolicyHash,
    call: { to: getAddress(release.factory), value: 0n, data: encodeFunctionData({ abi: launchFactoryCreateAbi, functionName: 'createLaunch', args: [{
      name: resolved.draft.metadata.name, symbol: resolved.draft.metadata.symbol, metadataHash: input.metadataHash,
      quote: getAddress(resolved.quote.address), quoteDecimals: resolved.quote.decimals,
      preset: presetValue, policy: policyValue, creatorChargeBps: resolved.draft.optionalCreatorChargeBps,
      recipients: resolved.draft.creatorRecipients.map(recipient => getAddress(recipient.wallet)), recipientWeightsBps: resolved.draft.creatorRecipients.map(recipient => recipient.weightBps),
    }] }) },
  };
}

/** Direct-wallet plain creation binds the reviewed graduation adapter and its
 * current runtime hash in the same transaction. It is calldata only: caller,
 * code, release and wallet checks remain mandatory immediately before send. */
export function encodeCheckedDirectLaunch(input: {
  config: LaunchConfig; draft: LaunchDraftInput; releaseId: string; metadataHash: `0x${string}`;
  expectedAdapter: string; expectedCodeHash: `0x${string}`; deadlineUnixSeconds: bigint; nowUnixSeconds: bigint;
}) {
  const context = checkedContext(input);
  const encoded = encodeApprovedLaunch(input);
  const { args: [params] } = decodeFunctionData({ abi: launchFactoryCreateAbi, data: encoded.call.data });
  const transaction: PreparedCall = { to: encoded.call.to, value: 0n,
    data: encodeFunctionData({ abi: launchFactoryCheckedCreateAbi, functionName: 'createLaunchChecked',
      args: [params, context.expectedAdapter, context.expectedCodeHash, context.deadlineUnixSeconds] }) };
  return { presetHash: encoded.presetHash, templatePolicyHash: encoded.templatePolicyHash,
    transaction, creator: getAddress(input.draft.vault), ...context };
}

/** Encode one atomic vault batch. Still requires live release/wrapper/allowlist
 * verification, current-owner authorization and simulation of the ENTIRE batch.
 * No arbitrary input conversion: native mode wraps the approved quote only.
 */
export function encodeApprovedLaunchWithFirstBuy(input: {
  config: LaunchConfig; draft: LaunchDraftInput; releaseId: string; metadataHash: `0x${string}`;
  maximumFeeBps: number; deadlineUnixSeconds: bigint; nowUnixSeconds: bigint;
  funding: { kind: 'quote' } | { kind: 'native'; approvedWrappedNative: `0x${string}` };
}) {
  const resolved = resolveLaunchDraft(input.config, input.draft);
  const firstBuy = resolved.draft.creatorFirstBuy;
  if (!firstBuy) throw new Error('Paid first buy is required');
  const maximumGross = BigInt(firstBuy.amountIn);
  const minimumBaseOut = BigInt(firstBuy.minimumAmountOut);
  const max128 = (1n << 128n) - 1n;
  if (maximumGross <= 0n || maximumGross > max128 || minimumBaseOut <= 0n || minimumBaseOut > max128) throw new Error('Invalid first-buy amount bounds');
  if (!Number.isInteger(input.maximumFeeBps) || input.maximumFeeBps < 0 || input.maximumFeeBps >= 10_000) throw new Error('Invalid maximum fee');
  if (input.nowUnixSeconds < 0n || input.deadlineUnixSeconds <= input.nowUnixSeconds
    || input.deadlineUnixSeconds > input.nowUnixSeconds + 300n) throw new Error('First-buy deadline must be within five minutes');
  const nativeFunding = input.funding.kind === 'native';
  const quote = getAddress(resolved.quote.address);
  if (input.funding.kind === 'native' && (resolved.quote.decimals !== 18 || !isAddressEqual(quote, input.funding.approvedWrappedNative))) throw new Error('Native funding requires the approved wrapped quote');
  // The separate plain-create API deliberately rejects a draft with a first buy.
  // Reuse its canonical preset/policy checks, then encode the first-buy entry point.
  const { creatorFirstBuy: _firstBuy, ...plainDraft } = resolved.draft;
  const encoded = encodeApprovedLaunch({ ...input, draft: plainDraft });
  const { args: [params] } = decodeFunctionData({ abi: launchFactoryCreateAbi, data: encoded.call.data });
  const call: PreparedCall = { to: encoded.call.to, value: nativeFunding ? maximumGross : 0n,
    data: encodeFunctionData({ abi: launchFactoryFirstBuyAbi, functionName: 'createLaunchWithFirstBuy',
      args: [params, maximumGross, minimumBaseOut, input.maximumFeeBps, input.deadlineUnixSeconds, nativeFunding] }) };
  const approval = (amount: bigint): PreparedCall => ({ to: quote, value: 0n,
    data: encodeFunctionData({ abi: approveAbi, functionName: 'approve', args: [call.to, amount] }) });
  return { presetHash: encoded.presetHash, templatePolicyHash: encoded.templatePolicyHash,
    calls: nativeFunding ? [call] : [approval(0n), approval(maximumGross), call, approval(0n)],
    maximumGross, minimumBaseOut, maximumFeeBps: input.maximumFeeBps, deadlineUnixSeconds: input.deadlineUnixSeconds,
    recipient: getAddress(resolved.draft.vault), refundAsset: quote, refundIsWrappedNative: nativeFunding,
  };
}

/** Direct-wallet paid creation is deliberately a sequence of separately
 * confirmed EOA transactions. ERC-20 approvals are not atomic with creation;
 * callers must journal/reconcile every step and execute the final zero reset
 * after success or failure. Native funding needs only the checked creation. */
export function encodeCheckedDirectLaunchWithFirstBuy(input: {
  config: LaunchConfig; draft: LaunchDraftInput; releaseId: string; metadataHash: `0x${string}`;
  maximumFeeBps: number; deadlineUnixSeconds: bigint; nowUnixSeconds: bigint;
  funding: { kind: 'quote' } | { kind: 'native'; approvedWrappedNative: `0x${string}` };
  expectedAdapter: string; expectedCodeHash: `0x${string}`;
}) {
  const context = checkedContext(input);
  const encoded = encodeApprovedLaunchWithFirstBuy(input);
  const creationIndex = input.funding.kind === 'native' ? 0 : 2;
  const original = encoded.calls[creationIndex];
  if (!original) throw new Error('Checked first-buy creation call unavailable');
  const decoded = decodeFunctionData({ abi: launchFactoryFirstBuyAbi, data: original.data });
  const [params, maximumGross, minimumBaseOut, maximumFeeBps, deadline, nativeFunding] = decoded.args;
  if (deadline !== context.deadlineUnixSeconds) throw new Error('Checked first-buy deadline mismatch');
  const checkedCreation: PreparedCall = { to: original.to, value: original.value,
    data: encodeFunctionData({ abi: launchFactoryCheckedFirstBuyAbi, functionName: 'createLaunchWithFirstBuyChecked',
      args: [params, maximumGross, minimumBaseOut, maximumFeeBps, deadline, nativeFunding,
        context.expectedAdapter, context.expectedCodeHash] }) };
  const transactions = encoded.calls.map((call, index) => index === creationIndex ? checkedCreation : call);
  const { calls: _atomicCalls, ...evidence } = encoded;
  return { ...evidence, transactions, creationIndex, creator: getAddress(input.draft.vault), ...context,
    requiresSequentialApprovalCleanup: input.funding.kind === 'quote' };
}
